import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, onSnapshot, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useCustomers } from '../hooks/useCustomers';
import { useTasks } from '../hooks/useTasks';
import { customerService } from '../lib/services/customerService';
import { leadService } from '../services/leadService';
import { OptimizedFormSubmissionService as FormSubmissionService } from '../services/optimizedFormSubmissionService';
import { FormTemplate } from '../types/formBuilder';
import { extractSubmissionContact, splitName, submissionsToCsv } from '../lib/formSubmissionUtils';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import Modal from '../components/common/Modal';
import SearchableSelect from '../components/common/SearchableSelect';
import {
  DocumentTextIcon, EyeIcon, CheckIcon, XMarkIcon, ArrowDownTrayIcon, RectangleStackIcon, UserPlusIcon,
  UserIcon, ClipboardDocumentCheckIcon, LinkIcon, EnvelopeIcon, PhoneIcon, BuildingOffice2Icon, InboxIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

type Status = 'draft' | 'submitted' | 'approved' | 'rejected';
interface Submission {
  id: string;
  templateId: string;
  templateName?: string;
  organizationId: string;
  submittedBy?: string;
  submittedByName?: string;
  status: Status;
  data: Record<string, any>;
  createdAt: any;
  leadId?: string;
  customerId?: string;
  taskId?: string;
  isPublic?: boolean;
}

const STATUS_META: Record<Status, { label: string; cls: string }> = {
  draft: { label: 'Vázlat', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  submitted: { label: 'Új', cls: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  approved: { label: 'Feldolgozva', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  rejected: { label: 'Elutasítva', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
};

const toDate = (v: any): Date => (v?.toDate ? v.toDate() : v instanceof Date ? v : new Date(v));
const fmt = (v: any) => { const d = toDate(v); return isNaN(d.getTime()) ? '–' : d.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); };

const FormSubmissions: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { customers } = useCustomers();
  const { createTask } = useTasks();
  const orgId = user?.organizationId;
  const canReview = hasPermission('forms', 'admin') || hasPermission('forms', 'access');

  const [subs, setSubs] = useState<Submission[]>([]);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | Status>('all');
  const templateFilter = params.get('template') || '';
  const [selected, setSelected] = useState<Submission | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [attachId, setAttachId] = useState('');

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    const u1 = onSnapshot(query(collection(db, 'formSubmissions'), where('organizationId', '==', orgId)), (snap) => {
      setSubs(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })).sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime()));
      setLoading(false);
    }, () => { setLoading(false); toast.error('A beküldések nem tölthetők be'); });
    const u2 = onSnapshot(query(collection(db, 'formTemplates'), where('organizationId', '==', orgId)), (snap) => {
      setTemplates(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    }, () => setTemplates([]));
    return () => { u1(); u2(); };
  }, [orgId]);

  const tplById = useMemo(() => new Map(templates.map(t => [t.id, t])), [templates]);
  const contactOf = (s: Submission) => extractSubmissionContact(tplById.get(s.templateId), s.data);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return subs.filter(s => {
      if (templateFilter && s.templateId !== templateFilter) return false;
      if (status !== 'all' && s.status !== status) return false;
      if (!term) return true;
      const c = contactOf(s);
      return [s.templateName, s.submittedByName, c.name, c.email, c.phone, c.company, c.summary].some(x => (x || '').toLowerCase().includes(term));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subs, search, status, templateFilter, templates]);

  const kpi = useMemo(() => ({
    all: subs.length,
    submitted: subs.filter(s => s.status === 'submitted').length,
    approved: subs.filter(s => s.status === 'approved').length,
    rejected: subs.filter(s => s.status === 'rejected').length
  }), [subs]);

  const patch = async (id: string, data: Record<string, any>) => updateDoc(doc(db, 'formSubmissions', id), { ...data, updatedAt: Timestamp.now() });

  const setReview = async (s: Submission, action: 'approve' | 'reject') => {
    try {
      setBusy(action);
      if (action === 'approve') await FormSubmissionService.approveSubmission(s.id); else await FormSubmissionService.rejectSubmission(s.id);
      setSelected(prev => prev && prev.id === s.id ? { ...prev, status: action === 'approve' ? 'approved' : 'rejected' } : prev);
      toast.success(action === 'approve' ? 'Feldolgozva' : 'Elutasítva');
    } catch { toast.error('Nem sikerült menteni'); } finally { setBusy(null); }
  };

  const createLead = async (s: Submission) => {
    if (!orgId) return;
    const c = contactOf(s);
    try {
      setBusy('lead');
      const id = await leadService.createLead({
        name: c.name || c.company || c.email || `Űrlap: ${s.templateName || ''}`,
        email: c.email || undefined, phone: c.phone || undefined, company: c.company || undefined,
        status: 'new', source: s.isPublic ? 'website' : 'manual', score: 0,
        notes: `Űrlap-beküldés (${s.templateName || ''}, ${fmt(s.createdAt)}):\n${c.summary}`
      } as any, orgId, user?.id, user?.displayName);
      await patch(s.id, { leadId: id });
      setSelected(prev => prev && prev.id === s.id ? { ...prev, leadId: id } : prev);
      toast.success('Lead létrehozva');
    } catch (e: any) { toast.error(e?.message || 'A lead létrehozása nem sikerült'); } finally { setBusy(null); }
  };

  const createCustomer = async (s: Submission) => {
    if (!orgId || !user) return;
    const c = contactOf(s);
    const { firstName, lastName } = splitName(c.name);
    try {
      setBusy('customer');
      const id = await customerService.create({
        type: c.company ? 'organization' : 'individual',
        companyName: c.company || undefined, firstName: firstName || undefined, lastName: lastName || undefined,
        email: c.email || '', phone: c.phone || '',
        address: { street: '', city: '', postalCode: '', country: 'Magyarország' },
        contactPerson: { name: c.name || '', email: c.email || '', phone: c.phone || '', position: '' },
        contactPersons: c.name ? [{ name: c.name, email: c.email || '', phone: c.phone || '', position: '', isPrimary: true }] : [],
        organizations: [orgId], notes: `Űrlapból (${s.templateName || ''}, ${fmt(s.createdAt)}):\n${c.summary}`,
        documents: [], isActive: true,
        ...( { organizationId: orgId, registrationSource: 'form' } as any )
      } as any, user.id);
      await patch(s.id, { customerId: id });
      setSelected(prev => prev && prev.id === s.id ? { ...prev, customerId: id } : prev);
      toast.success('Ügyfél létrehozva');
    } catch (e: any) { toast.error(e?.message || 'Az ügyfél létrehozása nem sikerült'); } finally { setBusy(null); }
  };

  const attachCustomer = async (s: Submission, customerId: string) => {
    if (!customerId) return;
    try {
      setBusy('attach');
      await patch(s.id, { customerId });
      setSelected(prev => prev && prev.id === s.id ? { ...prev, customerId } : prev);
      setAttachId('');
      toast.success('Ügyfélhez csatolva');
    } catch { toast.error('Nem sikerült csatolni'); } finally { setBusy(null); }
  };

  const createFollowUpTask = async (s: Submission) => {
    const c = contactOf(s);
    try {
      setBusy('task');
      const due = new Date(); due.setDate(due.getDate() + 1); due.setHours(17, 0, 0, 0);
      const id = await createTask({
        title: `Űrlap feldolgozása: ${c.name || c.company || c.email || 'ismeretlen'} – ${s.templateName || ''}`,
        description: c.summary, priority: 'medium', status: 'todo', assignedTo: user?.id, assignedToName: user?.displayName,
        dueDate: due, customerId: s.customerId, tags: ['űrlap'], documents: []
      });
      await patch(s.id, { taskId: id });
      setSelected(prev => prev && prev.id === s.id ? { ...prev, taskId: id } : prev);
      toast.success('Feladat létrehozva (holnap 17:00)');
    } catch { toast.error('A feladat létrehozása nem sikerült'); } finally { setBusy(null); }
  };

  const exportCsv = () => {
    const tpl = templateFilter ? tplById.get(templateFilter) || null : null;
    const csv = submissionsToCsv(tpl, filtered as any);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `urlap-bekuldesek${tpl ? '-' + tpl.name.replace(/[^\w-]+/g, '_') : ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const customerName = (id?: string) => { const c = customers.find(x => x.id === id); return c ? (c.companyName || [c.lastName, c.firstName].filter(Boolean).join(' ')) : ''; };

  if (loading) return <LoadingScreen />;

  const tile = (label: string, value: number, active: boolean, onClick: () => void, accent = '') => (
    <button type="button" onClick={onClick} className={`crm-card px-3 py-2 text-left hover:border-gray-400 transition-colors ${active ? 'ring-2 ring-primary-500/40' : ''}`}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-xl font-semibold leading-tight ${accent}`}>{value}</div>
    </button>
  );

  return (
    <div className="space-y-3 w-full min-w-0">
      <CommonPageHeader title="Űrlapok – beküldések" subtitle="Weboldali, ügyfélportálos és belső űrlapok kitöltései" icon={InboxIcon}>
        <button onClick={exportCsv} className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors" disabled={!filtered.length}>
          <ArrowDownTrayIcon className="h-4 w-4 mr-2" /><span>CSV export</span>
        </button>
        <button onClick={() => navigate('/form-builder')} className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:from-primary-600 hover:to-primary-700 transition-colors">
          <RectangleStackIcon className="h-4 w-4 mr-2" /><span>Űrlapok kezelése</span>
        </button>
      </CommonPageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tile('Összes', kpi.all, status === 'all', () => setStatus('all'))}
        {tile('Új', kpi.submitted, status === 'submitted', () => setStatus('submitted'), 'text-sky-600 dark:text-sky-300')}
        {tile('Feldolgozva', kpi.approved, status === 'approved', () => setStatus('approved'), 'text-emerald-600 dark:text-emerald-300')}
        {tile('Elutasítva', kpi.rejected, status === 'rejected', () => setStatus('rejected'), 'text-red-600 dark:text-red-300')}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1"><SearchBar value={search} onChange={setSearch} placeholder="Keresés névre, e-mailre, válaszra…" /></div>
        <select value={templateFilter} onChange={e => { const v = e.target.value; const p = new URLSearchParams(params); if (v) p.set('template', v); else p.delete('template'); setParams(p, { replace: true }); }} className="h-10 sm:w-64 pl-3 pr-8 text-sm border rounded-lg">
          <option value="">Minden űrlap</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="crm-card p-10 text-center">
          <InboxIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Nincs a szűrésnek megfelelő beküldés.</p>
          {templates.length === 0 && <button onClick={() => navigate('/form-builder')} className="crm-btn crm-btn-primary mt-4">Első űrlap létrehozása</button>}
        </div>
      ) : (
        <>
          {/* Asztali táblázat */}
          <div className="hidden md:block crm-card overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-4 text-left">Beküldő</th>
                  <th className="px-4 text-left">Űrlap</th>
                  <th className="px-4 text-left">Beküldve</th>
                  <th className="px-4 text-left">Állapot</th>
                  <th className="px-4 text-left">Kapcsolatok</th>
                  <th className="px-4 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const c = contactOf(s); const st = STATUS_META[s.status] || STATUS_META.submitted;
                  return (
                    <tr key={s.id} onClick={() => setSelected(s)} className="cursor-pointer">
                      <td className="px-4">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{c.name || c.company || s.submittedByName || 'Ismeretlen'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[260px]">{[c.email, c.phone].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className="px-4 text-gray-700 dark:text-gray-300">{s.templateName || tplById.get(s.templateId)?.name || '–'}</td>
                      <td className="px-4 whitespace-nowrap text-gray-600 dark:text-gray-400">{fmt(s.createdAt)}</td>
                      <td className="px-4"><span className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] font-medium ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4">
                        <div className="flex gap-1">
                          {s.customerId && <span title={customerName(s.customerId)} className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"><UserIcon className="w-3 h-3" />ügyfél</span>}
                          {s.leadId && <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"><UserPlusIcon className="w-3 h-3" />lead</span>}
                          {s.taskId && <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300"><ClipboardDocumentCheckIcon className="w-3 h-3" />feladat</span>}
                        </div>
                      </td>
                      <td className="px-4 text-right" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSelected(s)} className="p-1.5 rounded-md text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20" aria-label="Megnyitás"><EyeIcon className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobil kártyák */}
          <div className="md:hidden space-y-2">
            {filtered.map(s => {
              const c = contactOf(s); const st = STATUS_META[s.status] || STATUS_META.submitted;
              return (
                <div key={s.id} onClick={() => setSelected(s)} className="crm-card p-3 cursor-pointer">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.name || c.company || s.submittedByName || 'Ismeretlen'}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{s.templateName || tplById.get(s.templateId)?.name} · {fmt(s.createdAt)}</div>
                    </div>
                    <span className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] font-medium flex-shrink-0 ${st.cls}`}>{st.label}</span>
                  </div>
                  {(c.email || c.phone) && <div className="mt-1.5 text-xs text-gray-600 dark:text-gray-300 truncate">{[c.email, c.phone].filter(Boolean).join(' · ')}</div>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Részletek */}
      <Modal isOpen={!!selected} onClose={() => { setSelected(null); setAttachId(''); }} title="Beküldés részletei" size="3xl" noPadding>
        {selected && (() => {
          const s = selected; const c = contactOf(s); const st = STATUS_META[s.status] || STATUS_META.submitted;
          return (
            <div className="flex flex-col min-h-full">
              <div className="flex-1 px-4 sm:px-6 py-4 sm:py-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-300 flex items-center justify-center flex-shrink-0"><DocumentTextIcon className="w-5 h-5" /></div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight truncate">{s.templateName || tplById.get(s.templateId)?.name || 'Űrlap'}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{fmt(s.createdAt)} · {s.isPublic ? 'publikus link' : (s.submittedByName || 'belső kitöltés')}</p>
                  </div>
                  <span className={`inline-flex items-center h-7 px-2.5 rounded-full text-xs font-medium flex-shrink-0 ${st.cls}`}>{st.label}</span>
                </div>

                {(c.name || c.email || c.phone || c.company) && (
                  <div className="flex flex-wrap gap-1.5">
                    {c.name && <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"><UserIcon className="w-3.5 h-3.5 text-gray-400" />{c.name}</span>}
                    {c.company && <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"><BuildingOffice2Icon className="w-3.5 h-3.5 text-gray-400" />{c.company}</span>}
                    {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:text-primary-600"><EnvelopeIcon className="w-3.5 h-3.5 text-gray-400" />{c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:text-primary-600"><PhoneIcon className="w-3.5 h-3.5 text-gray-400" />{c.phone}</a>}
                  </div>
                )}

                <div className="crm-card px-4 py-1 divide-y divide-[var(--crm-border)]">
                  {c.rows.length === 0 && <div className="py-3 text-sm text-gray-500">Nincs kitöltött mező.</div>}
                  {c.rows.map((r, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 py-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 flex-shrink-0 max-w-[45%]">{r.label}</span>
                      <span className="text-right font-medium text-gray-900 dark:text-gray-100 min-w-0 whitespace-pre-wrap break-words">
                        {r.type === 'signature' && typeof r.raw === 'string' && r.raw.startsWith('data:image') ? <img src={r.raw} alt="aláírás" className="h-12 ml-auto bg-white rounded border border-gray-200" />
                          : (r.type === 'file' || r.type === 'image') && r.raw?.url ? <a href={r.raw.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline inline-flex items-center gap-1">{r.raw.name || 'fájl'}<ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /></a>
                          : r.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Kapcsolatok */}
                <div className="crm-card p-4 space-y-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">CRM-kapcsolatok</div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.customerId ? <button onClick={() => navigate(`/customers?open=${s.customerId}`)} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300"><UserIcon className="w-3.5 h-3.5" />Ügyfél: {customerName(s.customerId) || s.customerId}</button> : null}
                    {s.leadId ? <button onClick={() => navigate('/leads')} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"><UserPlusIcon className="w-3.5 h-3.5" />Lead létrehozva</button> : null}
                    {s.taskId ? <button onClick={() => navigate('/tasks')} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300"><ClipboardDocumentCheckIcon className="w-3.5 h-3.5" />Feladat létrehozva</button> : null}
                    {!s.customerId && !s.leadId && !s.taskId && <span className="text-xs text-gray-500 dark:text-gray-400">Még nincs ügyfélhez, leadhez vagy feladathoz kötve.</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {!s.leadId && <button onClick={() => createLead(s)} disabled={!!busy} className="crm-btn crm-btn-secondary h-8 text-xs"><UserPlusIcon className="w-3.5 h-3.5" />Lead ebből</button>}
                    {!s.customerId && <button onClick={() => createCustomer(s)} disabled={!!busy} className="crm-btn crm-btn-secondary h-8 text-xs"><UserIcon className="w-3.5 h-3.5" />Ügyfél ebből</button>}
                    {!s.taskId && <button onClick={() => createFollowUpTask(s)} disabled={!!busy} className="crm-btn crm-btn-secondary h-8 text-xs"><ClipboardDocumentCheckIcon className="w-3.5 h-3.5" />Feladat</button>}
                  </div>
                  {!s.customerId && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <div className="flex-1 min-w-0">
                        <SearchableSelect options={customers.map(cu => ({ value: cu.id, label: cu.companyName || [cu.lastName, cu.firstName].filter(Boolean).join(' ') || cu.email }))} value={attachId} onChange={setAttachId} placeholder="Csatolás meglévő ügyfélhez…" clearable />
                      </div>
                      <button onClick={() => attachCustomer(s, attachId)} disabled={!attachId || !!busy} className="crm-btn crm-btn-secondary h-9 text-xs"><LinkIcon className="w-3.5 h-3.5" />Csatolás</button>
                    </div>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 z-10 flex flex-wrap justify-end gap-2 px-4 sm:px-6 py-3 border-t border-[var(--crm-border)] bg-[var(--crm-surface)]">
                {canReview && s.status === 'submitted' && (
                  <>
                    <button onClick={() => setReview(s, 'reject')} disabled={!!busy} className="crm-btn crm-btn-secondary text-red-600 mr-auto"><XMarkIcon className="w-4 h-4" />Elutasít</button>
                    <button onClick={() => setReview(s, 'approve')} disabled={!!busy} className="crm-btn crm-btn-primary"><CheckIcon className="w-4 h-4" />Feldolgozva</button>
                  </>
                )}
                <button onClick={() => { setSelected(null); setAttachId(''); }} className="crm-btn crm-btn-secondary">Bezárás</button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default FormSubmissions;
